<script setup lang="ts">
/**
 * Campaign preview for content cards: its layout at the canvas's aspect ratio, each section showing its first
 * item at its fit (the item's, else the campaign's, else Fit) — the thumbnail of an image or video, a built-in app's own (paused) preview — else the section colour.
 */
import type { Layout, MediaAssetRecord } from '@codot/contracts';
import ZoneWidget from './apps/ZoneWidget.vue';

defineProps<{ campaign: Layout; assets: Map<string, MediaAssetRecord> }>();
const COLORS = ['#6366f1', '#22c55e', '#f97316', '#ec4899', '#06b6d4', '#eab308'];
/** The item's fit, else the campaign's, else Fit (whole item) — what the player shows by default. */
const FIT = { fit: 'object-contain', fill: 'object-cover', stretch: 'object-fill' } as const;
const pct = (v: number, of: number) => `${(v / of) * 100}%`;
</script>

<template>
  <div class="flex h-full w-full items-center justify-center bg-slate-900" aria-hidden="true">
    <div
      class="relative max-h-full max-w-full"
      :style="{ aspectRatio: `${campaign.canvasWidth} / ${campaign.canvasHeight}`, height: '100%' }"
    >
      <div
        v-for="(z, zi) in campaign.zones"
        :key="z.id"
        class="absolute overflow-hidden outline outline-1 -outline-offset-1 outline-slate-900"
        :style="{
          left: pct(z.x, campaign.canvasWidth),
          top: pct(z.y, campaign.canvasHeight),
          width: pct(z.width, campaign.canvasWidth),
          height: pct(z.height, campaign.canvasHeight),
          zIndex: zi + 1,
          // A picture's letterbox bars are black, as on the player; an empty section shows its colour.
          background: z.items[0] && assets.get(z.items[0].assetId)?.thumbnailUrl ? '#000' : COLORS[zi % COLORS.length],
        }"
      >
        <template v-for="a in [z.items[0] ? assets.get(z.items[0].assetId) : undefined]" :key="a?.id ?? 'none'">
          <img v-if="a?.thumbnailUrl" :src="a.thumbnailUrl" alt="" class="h-full w-full" :class="FIT[z.items[0]?.fit ?? campaign.fit ?? 'fit']" loading="lazy" />
          <ZoneWidget v-else-if="a?.config" :config="a.config" :playing="false" :width="z.width" :height="z.height" class="pointer-events-none" />
        </template>
      </div>
    </div>
  </div>
</template>
